import React from 'react';
import type { Portfolio } from 'venn-api';
import type { AllocConstraint } from 'venn-utils';
import AddNewConstraint from './components/AddNewConstraint';
import EditableAllocationConstraint from './components/EditableAllocationConstraint';
import { ConditionalOverlay } from '../../conditional-overlay';
import { RelativeContainer, RelativeSpinner } from '../components/styled';
import { LoadingSize } from 'venn-ui-kit';

interface AllocationConstraintsManagementProps {
  portfolio: Portfolio;
  allocationConstraints: AllocConstraint[];
  addNewConstraintClassName?: string;
  onNewConstraintsAdded: (constraints: AllocConstraint[]) => void;
  setConstraints: (constraint: AllocConstraint[]) => void;
  isAddingConstraint: boolean;
  setIsAddingConstraint: (isAddingConstraint: boolean) => void;
  isSavingConstraint: boolean;
}

const AllocationConstraintsManagement = ({
  portfolio,
  allocationConstraints,
  addNewConstraintClassName,
  onNewConstraintsAdded,
  setConstraints,
  isAddingConstraint,
  setIsAddingConstraint,
  isSavingConstraint,
}: AllocationConstraintsManagementProps) => {
  if (allocationConstraints.length === 0 && !isAddingConstraint) {
    return <AddNewConstraint onClick={() => setIsAddingConstraint(true)} />;
  }

  return (
    <div>
      {allocationConstraints.map((constraint, idx) => (
        <EditableAllocationConstraint
          key={getConstraintKey(constraint, idx)}
          existingConstraint={constraint}
          portfolio={portfolio}
          idx={idx + 1}
          onDeleteConstraint={() => {
            const removeDeleted = [...allocationConstraints.slice(0, idx), ...allocationConstraints.slice(idx + 1)];
            setConstraints(removeDeleted);
          }}
          onCreateConstraint={(constraints: AllocConstraint[]) => {
            const updatedConstraints = [
              ...allocationConstraints.slice(0, idx),
              ...constraints,
              ...allocationConstraints.slice(idx + 1),
            ];
            setConstraints(updatedConstraints);
          }}
        />
      ))}
      {(isAddingConstraint || isSavingConstraint) && (
        <RelativeContainer>
          <ConditionalOverlay condition={isSavingConstraint} overlay="">
            <EditableAllocationConstraint
              portfolio={portfolio}
              idx={allocationConstraints.length + 1}
              onCreateConstraint={onNewConstraintsAdded}
              onDeleteConstraint={() => setIsAddingConstraint(false)}
            />
          </ConditionalOverlay>
          {isSavingConstraint && <RelativeSpinner size={LoadingSize.nano} />}
        </RelativeContainer>
      )}
      {!isAddingConstraint && (
        <AddNewConstraint className={addNewConstraintClassName} onClick={() => setIsAddingConstraint(true)} />
      )}
    </div>
  );
};

export default AllocationConstraintsManagement;

const getConstraintKey = (constraint: AllocConstraint, idx: number): string =>
  `${constraint.allInvestments ? 'allInv' : constraint.items.map((item) => item.id).join(',')}#${
    constraint.condition
  }#${constraint.valueType}#${constraint.value}#${idx}`;
